import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PageHeader } from '@arco-design/web-react';
import FieldTip from './FieldTip';
import { navDomainLabelOf, navTitleOf } from '../registry/features';
import { setBaseTitle } from '../utils/ringAlert';

/**
 * 内嵌内容标记：设置 Landing 的 TabPane 内用 `<EmbeddedPage>` 包住内容块，
 * 块内的 `PageContainer` 便不再渲染页头（标题由外层 Landing 承担，避免两层标题）。
 *
 * 这是「把整页塞进 Tab」场景的收口点：此前靠 5 个页级 CSS 用 `display:none` 抹掉内嵌页头，
 * 副作用是把页头唯一承载模块说明的 `titleTip` 一起抹掉——嵌入态与直连同一 URL 看到的信息量不一致。
 *
 * `active` 表示「当前可见的那个内嵌块」：Arco Tabs 会把访问过的 TabPane 留在 DOM 里，
 * 只有激活块才该把页头操作（保存/重置）交给外层页头，否则多个 Tab 会抢同一个插槽。
 */
const EmbeddedContext = createContext<{ active: boolean } | null>(null);

/** 内嵌内容包裹：见 `EmbeddedContext` 说明 */
export function EmbeddedPage({ children, active = true }: { children: ReactNode; active?: boolean }) {
  // 必须 memo：消费方（SettingsPage 的注册 effect）以「是否激活」为依赖，
  // 每次渲染新建对象会让依赖恒变 → effect 每帧重跑 → setState → 再渲染（实测会触发
  // "Maximum update depth exceeded" 的无限循环）
  const value = useMemo(() => ({ active }), [active]);
  return <EmbeddedContext.Provider value={value}>{children}</EmbeddedContext.Provider>;
}

/** 读取内嵌态（非内嵌返回 null）；`active` 见 `EmbeddedPage` 说明 */
export function useEmbedded() {
  const state = useContext(EmbeddedContext);
  return state;
}

interface Props {
  /**
   * 兜底标题：**只在当前地址解析不出导航名时才用**（旧地址一对多、详情页、全屏页）。
   * canonical 路由与可唯一判定的旧地址一律由 Registry 的菜单名/锚点名决定——页面不参与命名，
   * 否则就会出现「按菜单『知识分析』点进来，页头却写『统计看板』」这类漂移
   * （口径见 `docs/conventions/information-architecture.md §14`）。
   */
  title?: string;
  /** 页头名后缀标记（如运营监控的「实时」徽标）：只加标记、不改名，不参与命名口径 */
  titleSuffix?: ReactNode;
  /** 标题右侧问号说明（悬浮 Tooltip；页头说明默认问号悬浮、不塞 subTitle，见 frontend-dev-convention §8.5） */
  titleTip?: ReactNode;
  subTitle?: string;
  extra?: ReactNode;
  /** 追加到 .bcc-page-body 的类名（如坐席工作台 agw-page：固定视口布局，见 pages/workbench/agw/shell.css） */
  className?: string;
  children: ReactNode;
}

export default function PageContainer({ title, titleSuffix, titleTip, subTitle, extra, className, children }: Props) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const [headerH, setHeaderH] = useState<number>();
  const { pathname } = useLocation();
  const embedded = useContext(EmbeddedContext);
  // 页头名（唯一真源）：导航名优先，页面 title 只作兜底——见 Props.title 说明
  const navName = navTitleOf(pathname);
  const fallbackName = typeof title === 'string' ? title.trim() : '';
  const name = navName || fallbackName;

  // 浏览器标签标题随页面变化：坐席常同时开工作台/呼叫记录/工单多个标签，标题恒定则无法分辨。
  // 写标题的唯一出口是 ringAlert 的 setBaseTitle——来电与风险提醒正在闪烁时它不覆盖，
  // 否则会把闪烁文案打断（恢复时也统一回到这里登记的页面标题）。
  // 后缀用业务域（如「质检 · 智能质检」）而不是分区（业务/平台/系统）：页头名是菜单条目名，
  // 域才是它在侧栏里的位置；四个域各有一条「设置」，只写分区在标签栏上无从分辨。
  // 内嵌态跳过：此时页面标题由外层 Landing 决定，内层再写会把外层标题顶掉。
  useEffect(() => {
    if (embedded) return;
    setBaseTitle([name, navDomainLabelOf(pathname)].filter(Boolean).join(' · '));
  }, [pathname, name, embedded]);

  // 实测页面标题栏高度 → 写入 --bcc-header-h，供 Tab 栏 sticky 偏移（标题常驻、Tab 紧随其下）
  useEffect(() => {
    if (embedded) return;
    const el = bodyRef.current?.querySelector<HTMLElement>('.bcc-page-header');
    if (!el) return;
    const measure = () => setHeaderH(el.getBoundingClientRect().height);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [embedded]);

  // 内嵌态：不渲染页头；页头 extra 里的动作（若有）下沉为一行右对齐操作区，避免操作入口在嵌入态消失
  if (embedded) {
    return (
      <div className={className ? `bcc-page-body bcc-page-body-embedded ${className}` : 'bcc-page-body bcc-page-body-embedded'}>
        {extra ? <div className="bcc-embed-actions">{extra}</div> : null}
        {children}
      </div>
    );
  }

  return (
    <div ref={bodyRef} className={className ? `bcc-page-body ${className}` : 'bcc-page-body'} style={{ ['--bcc-header-h' as string]: headerH ? `${headerH}px` : undefined }}>
      <PageHeader
        className="bcc-page-header"
        title={
          <>
            {/* 页头名的稳定落点：后缀徽标/问号说明都在它之外，便于门禁与回归断言「页头名 == 菜单名」 */}
            <span className="bcc-page-title-name">{name}</span>
            {titleSuffix ? <span className="bcc-page-title-suffix">{titleSuffix}</span> : null}
            {titleTip ? <FieldTip text={titleTip} className="bcc-page-title-tip" /> : null}
          </>
        }
        subTitle={subTitle}
        extra={extra}
      />
      {children}
    </div>
  );
}
